import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Users, 
  CreditCard, 
  TrendingUp, 
  Calendar,
  Loader2,
  Search,
  Edit2,
  ShieldCheck,
  Crown,
  User,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Eye,
  Mail,
  Phone,
  MapPin,
  Target,
  Dumbbell,
  Scale,
  Ruler,
  Award,
  ClipboardList,
  Download,
  Trash2,
  Bell,
  Euro,
  Globe,
  Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { findCountry } from '../lib/countries';
import { Flag } from '../components/Flag';
import RevenueTab from '../components/admin/RevenueTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('cardioai_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Users
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [userFilter, setUserFilter] = useState('');
  const [exporting, setExporting] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [reminderStats, setReminderStats] = useState(null);
  const [pwaStats, setPwaStats] = useState(null);

  // Permanently delete a user and their data
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      await axios.delete(`${API}/admin/users/${deletingUser.id}`, { headers: getAuthHeaders() });
      toast.success(`${deletingUser.email} apagado`);
      setDeletingUser(null);
      fetchUsers(usersPage, userFilter);

      // Refresh stats
      const statsRes = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao apagar utilizador');
    }
  };
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [selectedUserPlansCount, setSelectedUserPlansCount] = useState(0);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Subscription Plans
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ name: '', price: 0, features: '', daily_messages: 0, monthly_plans: 0 });

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Acesso negado. Apenas administradores.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes, plansRes, reminderRes, pwaRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/users?limit=10`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/subscription-plans`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/reminder-stats`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/pwa-stats`, { headers: getAuthHeaders() })
        ]);
        
        setStats(statsRes.data);
        setUsers(usersRes.data.users);
        setUsersTotal(usersRes.data.total);
        setPlans(plansRes.data.plans);
        setReminderStats(reminderRes.data);
        setPwaStats(pwaRes.data);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        if (error.response?.status === 403) {
          toast.error('Acesso negado. Apenas administradores.');
          navigate('/dashboard');
        } else {
          toast.error('Erro ao carregar dados');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user, navigate]);

  // Fetch users with pagination
  const fetchUsers = async (page = 0, plan = '') => {
    try {
      const params = new URLSearchParams({ skip: page * 10, limit: 10 });
      if (plan) params.append('plan', plan);
      
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: getAuthHeaders() });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
      setUsersPage(page);
    } catch (error) {
      toast.error('Erro ao carregar utilizadores');
    }
  };

  // Export all users as CSV
  const handleExportUsers = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/users/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `utilizadores_processbody_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exportação concluída!');
    } catch (error) {
      toast.error('Erro ao exportar utilizadores');
    } finally {
      setExporting(false);
    }
  };

  // Fetch user profile details
  const fetchUserProfile = async (userId) => {
    setLoadingProfile(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      setSelectedUser(res.data.user);
      setSelectedUserProfile(res.data.profile);
      setSelectedUserPlansCount(res.data.plans_count || 0);
    } catch (error) {
      toast.error('Erro ao carregar perfil');
      setSelectedUser(null);
      setSelectedUserProfile(null);
      setSelectedUserPlansCount(0);
    } finally {
      setLoadingProfile(false);
    }
  };

  const closeProfileModal = () => {
    setSelectedUser(null);
    setSelectedUserProfile(null);
    setSelectedUserPlansCount(0);
  };

  // Update user
  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        plan: editingUser.plan,
        role: editingUser.role
      }, { headers: getAuthHeaders() });
      
      toast.success('Utilizador atualizado com sucesso');
      setEditingUser(null);
      fetchUsers(usersPage, userFilter);
      
      // Refresh stats
      const statsRes = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao atualizar utilizador');
    }
  };

  // Update subscription plan
  const handleUpdatePlan = async () => {
    if (!editingPlan) return;
    
    try {
      const features = planForm.features.split('\n').filter(f => f.trim());
      
      await axios.put(`${API}/admin/subscription-plans/${editingPlan}`, {
        name: planForm.name,
        price: parseFloat(planForm.price),
        features: features,
        daily_messages: parseInt(planForm.daily_messages),
        monthly_plans: parseInt(planForm.monthly_plans)
      }, { headers: getAuthHeaders() });
      
      toast.success('Plano atualizado com sucesso');
      setEditingPlan(null);
      
      // Refresh plans
      const plansRes = await axios.get(`${API}/admin/subscription-plans`, { headers: getAuthHeaders() });
      setPlans(plansRes.data.plans);
    } catch (error) {
      toast.error('Erro ao atualizar plano');
    }
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan.id);
    setPlanForm({
      name: plan.name,
      price: plan.price,
      features: plan.features.join('\n'),
      daily_messages: plan.daily_messages,
      monthly_plans: plan.monthly_plans ?? 0
    });
  };

  const planBadgeColors = {
    free: 'bg-gray-500/20 text-gray-400',
    pro: 'bg-blue-500/20 text-blue-400',
    premium: 'bg-yellow-500/20 text-yellow-400'
  };

  const roleBadgeColors = {
    user: 'bg-gray-500/20 text-gray-400',
    admin: 'bg-red-500/20 text-red-400'
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="admin-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 text-red-500 flex-shrink-0" />
            <h1 
              className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Painel de <span className="text-red-500">Administrador</span>
            </h1>
          </div>
          <p className="text-gray-400">
            Gere utilizadores, planos de subscrição e monitoriza a plataforma
          </p>
        </div>

        {/* Tabs */}
        <div className="tab-scroll mb-6">
          {[
            { id: 'stats', label: 'Estatísticas', icon: TrendingUp },
            { id: 'revenue', label: 'Receita', icon: Euro },
            { id: 'users', label: 'Utilizadores', icon: Users },
            { id: 'plans', label: 'Planos', icon: CreditCard }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                  : 'bg-black/30 text-gray-400 border border-white/10 hover:border-white/30'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Revenue Tab */}
        {activeTab === 'revenue' && <RevenueTab />}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500">Total Utilizadores</p>
                      <p className="text-2xl sm:text-3xl font-bold text-white">{stats.total_users}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 hidden sm:flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500">Novos esta Semana</p>
                      <p className="text-2xl sm:text-3xl font-bold text-[#32CD32]">{stats.new_users_this_week}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-[#32CD32]/20 hidden sm:flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-[#32CD32]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500">Planos Gerados</p>
                      <p className="text-2xl sm:text-3xl font-bold text-purple-400">{stats.total_plans_generated}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-purple-500/20 hidden sm:flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500">Utilizadores Pagos</p>
                      <p className="text-2xl sm:text-3xl font-bold text-yellow-400">
                        {stats.users_by_plan.pro + stats.users_by_plan.premium}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-yellow-500/20 hidden sm:flex items-center justify-center flex-shrink-0">
                      <Crown className="w-6 h-6 text-yellow-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Plan Distribution */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-lg uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  Distribuição por Plano
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { name: 'Grátis', key: 'free', color: 'bg-gray-500' },
                    { name: 'Pro', key: 'pro', color: 'bg-blue-500' },
                    { name: 'Premium', key: 'premium', color: 'bg-yellow-500' }
                  ].map(plan => {
                    const count = stats.users_by_plan[plan.key];
                    const percentage = stats.total_users > 0 ? (count / stats.total_users) * 100 : 0;
                    return (
                      <div key={plan.key} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">{plan.name}</span>
                          <span>{count} ({percentage.toFixed(1)}%)</span>
                        </div>
                        <div className="h-2 bg-black/50 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${plan.color} transition-all duration-500`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Reminder effectiveness */}
            {pwaStats && (
              <Card className="bg-[#121212] border-white/10" data-testid="pwa-stats-card">
                <CardHeader>
                  <CardTitle className="text-lg uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    <Smartphone className="w-5 h-5 text-[#32CD32]" />
                    Instalações da App
                  </CardTitle>
                  <CardDescription className="text-[#A1A1AA]">
                    Quantas pessoas instalaram o Process Body no telemóvel
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pwaStats.total_installs === 0 ? (
                    <p className="text-sm text-gray-400" data-testid="pwa-stats-empty">
                      Ainda não há instalações registadas. O convite para instalar aparece no Dashboard de cada cliente.
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Total instalações</p>
                          <p className="text-2xl font-bold text-white" data-testid="pwa-total-installs">
                            {pwaStats.total_installs}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Últimos 30 dias</p>
                          <p className="text-2xl font-bold text-[#32CD32]" data-testid="pwa-installs-month">
                            {pwaStats.installs_last_30_days}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">% dos clientes</p>
                          <p className="text-2xl font-bold text-blue-400" data-testid="pwa-install-rate">
                            {pwaStats.install_rate}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Ativos na app (7d)</p>
                          <p className="text-2xl font-bold text-purple-400" data-testid="pwa-active-users">
                            {pwaStats.active_app_users_7_days}
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-white/5 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        {Object.entries(pwaStats.by_platform).map(([platform, count]) => (
                          <span key={platform} className="text-gray-400">
                            {platform === 'ios' ? 'iPhone' : platform === 'android' ? 'Android' : 'Computador'}:{' '}
                            <span className="text-white font-semibold">{count}</span>
                          </span>
                        ))}
                        <span className="text-gray-400">
                          Aberturas (30d): <span className="text-white font-semibold">{pwaStats.launches_last_30_days}</span>
                        </span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {reminderStats && (
              <Card className="bg-[#121212] border-white/10" data-testid="reminder-stats-card">
                <CardHeader>
                  <CardTitle className="text-lg uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    <Bell className="w-5 h-5 text-[#32CD32]" />
                    Eficácia dos Lembretes
                  </CardTitle>
                  <CardDescription className="text-[#A1A1AA]">
                    Quantas pessoas voltaram a treinar nos {reminderStats.return_window_days || 7} dias
                    seguintes a receber o lembrete
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {reminderStats.total_sent === 0 ? (
                    <p className="text-[#A1A1AA] text-sm py-4" data-testid="reminder-stats-empty">
                      Ainda não foram enviados lembretes. Os dados aparecem aqui depois do
                      primeiro envio automático (todos os dias às 09:00).
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                          <p className="text-xs text-[#A1A1AA] uppercase mb-1">Lembretes enviados</p>
                          <p className="text-2xl font-bold" data-testid="reminder-total-sent">
                            {reminderStats.total_sent}
                          </p>
                        </div>
                        <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                          <p className="text-xs text-[#A1A1AA] uppercase mb-1">Voltaram a treinar</p>
                          <p className="text-2xl font-bold text-[#32CD32]" data-testid="reminder-returned">
                            {reminderStats.returned}
                          </p>
                        </div>
                        <div className="bg-black/30 border border-[#32CD32]/30 rounded-lg p-4">
                          <p className="text-xs text-[#A1A1AA] uppercase mb-1">Taxa de regresso</p>
                          <p className="text-2xl font-bold text-[#32CD32]" data-testid="reminder-conversion">
                            {reminderStats.conversion_rate}%
                          </p>
                        </div>
                        <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                          <p className="text-xs text-[#A1A1AA] uppercase mb-1">Tempo médio</p>
                          <p className="text-2xl font-bold">
                            {reminderStats.avg_days_to_return != null
                              ? `${reminderStats.avg_days_to_return}d`
                              : '—'}
                          </p>
                        </div>
                      </div>

                      <div className="bg-black/20 border border-white/10 rounded-lg p-4 mb-6">
                        <p className="text-xs text-[#A1A1AA] uppercase mb-2">Últimos 30 dias</p>
                        <p className="text-sm text-gray-200" data-testid="reminder-last-30">
                          {reminderStats.last_30_days.sent} enviados &middot;{' '}
                          <span className="text-[#32CD32]">
                            {reminderStats.last_30_days.returned} voltaram
                          </span>{' '}
                          ({reminderStats.last_30_days.conversion_rate || 0}%)
                        </p>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs text-[#A1A1AA] uppercase mb-2">Envios recentes</p>
                        {reminderStats.recent.map((r, i) => (
                          <div
                            key={i}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 px-3 bg-black/20 border border-white/5 rounded-lg"
                          >
                            <div className="min-w-0">
                              <p className="text-sm text-gray-200 truncate">{r.email}</p>
                              <p className="text-xs text-[#A1A1AA]">
                                {new Date(r.sent_at).toLocaleDateString('pt-PT')} &middot;{' '}
                                {r.days_inactive} dias sem treinar &middot; {r.channels.join(', ')}
                              </p>
                            </div>
                            <span
                              className={`text-xs px-2 py-1 rounded-full whitespace-nowrap w-fit ${
                                r.returned
                                  ? 'bg-[#32CD32]/20 text-[#32CD32] border border-[#32CD32]/30'
                                  : 'bg-white/5 text-[#A1A1AA] border border-white/10'
                              }`}
                            >
                              {r.returned ? 'Voltou' : 'Sem regresso'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Filters */}
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <Select 
                      value={userFilter || "all"} 
                      onValueChange={(value) => {
                        const filterValue = value === "all" ? "" : value;
                        setUserFilter(filterValue);
                        fetchUsers(0, filterValue);
                      }}
                    >
                      <SelectTrigger className="bg-black/50 border-white/20">
                        <SelectValue placeholder="Filtrar por plano" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/20">
                        <SelectItem value="all">Todos os planos</SelectItem>
                        <SelectItem value="free">Grátis</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-sm text-gray-500 flex items-center">
                    Total: {usersTotal} utilizadores
                  </div>
                  <Button
                    onClick={handleExportUsers}
                    disabled={exporting}
                    className="btn-primary rounded-lg flex items-center gap-2"
                    data-testid="export-users-csv-btn"
                  >
                    <Download className="w-4 h-4" />
                    {exporting ? 'A exportar...' : 'Exportar Utilizadores (CSV)'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Users List */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-lg uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  Lista de Utilizadores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map(u => (
                    <div 
                      key={u.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-black/30 rounded-xl border border-white/5 hover:border-white/20 transition-colors"
                    >
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-[#32CD32]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-[#32CD32] font-bold">
                            {u.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{u.name}</p>
                          <p className="text-sm text-gray-500 truncate">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${planBadgeColors[u.plan]}`}>
                          {u.plan?.toUpperCase()}
                        </span>
                        {u.role === 'admin' && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadgeColors.admin}`}>
                            ADMIN
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => fetchUserProfile(u.id)}
                          className="text-[#32CD32] hover:text-[#32CD32] hover:bg-[#32CD32]/10"
                          data-testid={`view-profile-${u.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingUser(u)}
                          className="text-gray-400 hover:text-white"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {u.role !== 'admin' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeletingUser(u)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/10">
                  <Button
                    variant="ghost"
                    onClick={() => fetchUsers(usersPage - 1, userFilter)}
                    disabled={usersPage === 0}
                    className="text-gray-400"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-500">
                    Página {usersPage + 1} de {Math.ceil(usersTotal / 10)}
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => fetchUsers(usersPage + 1, userFilter)}
                    disabled={(usersPage + 1) * 10 >= usersTotal}
                    className="text-gray-400"
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Edit User Modal */}
            {editingUser && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                <Card className="w-full max-w-md bg-[#121212] border-white/10 max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <CardTitle className="text-lg">Editar Utilizador</CardTitle>
                    <CardDescription>{editingUser.name} ({editingUser.email})</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Plano</Label>
                      <Select 
                        value={editingUser.plan} 
                        onValueChange={(value) => setEditingUser({ ...editingUser, plan: value })}
                      >
                        <SelectTrigger className="bg-black/50 border-white/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/20">
                          <SelectItem value="free">Grátis</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Função</Label>
                      <Select 
                        value={editingUser.role || 'user'} 
                        onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                      >
                        <SelectTrigger className="bg-black/50 border-white/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/20">
                          <SelectItem value="user">Utilizador</SelectItem>
                          <SelectItem value="admin">Administrador</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="ghost"
                        onClick={() => setEditingUser(null)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleUpdateUser}
                        className="flex-1 bg-[#32CD32] hover:bg-[#2ab32a] text-black"
                        data-testid="save-user-btn"
                      >
                        Guardar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* View Profile Modal */}
            {(selectedUser || loadingProfile) && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                <Card className="w-full max-w-2xl bg-[#121212] border-white/10 max-h-[90vh] overflow-y-auto">
                  {loadingProfile ? (
                    <CardContent className="py-12 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
                    </CardContent>
                  ) : (
                    <>
                      <CardHeader className="border-b border-white/10">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#32CD32]/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-[#32CD32] font-bold text-xl">
                                {selectedUser?.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <CardTitle className="text-lg sm:text-xl truncate">{selectedUser?.name}</CardTitle>
                              <CardDescription className="flex items-center gap-2 min-w-0">
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{selectedUser?.email}</span>
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${planBadgeColors[selectedUser?.plan]}`}>
                              {selectedUser?.plan?.toUpperCase()}
                            </span>
                            {selectedUser?.role === 'admin' && (
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleBadgeColors.admin}`}>
                                ADMIN
                              </span>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-6">
                        {selectedUserProfile ? (
                          <div className="space-y-6">
                            {/* Personal Info */}
                            <div>
                              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                                Dados Pessoais
                              </h3>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div className="bg-black/30 rounded-lg p-3">
                                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                    <Mail className="w-3 h-3" />
                                    Email
                                  </div>
                                  <p className="font-medium text-sm break-all">{selectedUser?.email}</p>
                                </div>
                                {selectedUserProfile.phone && (
                                  <div className="bg-black/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                      <Phone className="w-3 h-3" />
                                      Telefone
                                    </div>
                                    <p className="font-medium">{selectedUserProfile.phone}</p>
                                  </div>
                                )}
                                <div className="bg-black/30 rounded-lg p-3">
                                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                    <User className="w-3 h-3" />
                                    Idade
                                  </div>
                                  <p className="font-medium">{selectedUserProfile.age} anos</p>
                                </div>
                                <div className="bg-black/30 rounded-lg p-3">
                                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                    <Scale className="w-3 h-3" />
                                    Peso
                                  </div>
                                  <p className="font-medium">{selectedUserProfile.weight} kg</p>
                                </div>
                                <div className="bg-black/30 rounded-lg p-3">
                                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                    <Ruler className="w-3 h-3" />
                                    Altura
                                  </div>
                                  <p className="font-medium">{selectedUserProfile.height} cm</p>
                                </div>
                                <div className="bg-black/30 rounded-lg p-3">
                                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                    <Award className="w-3 h-3" />
                                    Nivel
                                  </div>
                                  <p className="font-medium capitalize">
                                    {selectedUserProfile.experience_level === 'beginner' ? 'Iniciante' :
                                     selectedUserProfile.experience_level === 'intermediate' ? 'Intermediario' : 'Avancado'}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Contact Info - Location and Gender */}
                            {(selectedUserProfile.location || selectedUserProfile.gender || selectedUserProfile.nationality) && (
                              <div>
                                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                                  Informacoes Adicionais
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {selectedUserProfile.nationality && (
                                    <div className="bg-black/30 rounded-lg p-3" data-testid="admin-profile-nationality">
                                      <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                        <Globe className="w-3 h-3" />
                                        Nacionalidade
                                      </div>
                                      <p className="font-medium flex items-center gap-2">
                                        {findCountry(selectedUserProfile.nationality) ? (
                                          <>
                                            <Flag code={selectedUserProfile.nationality} size={20} />
                                            {findCountry(selectedUserProfile.nationality).name}
                                          </>
                                        ) : (
                                          selectedUserProfile.nationality
                                        )}
                                      </p>
                                    </div>
                                  )}
                                  {selectedUserProfile.gender && (
                                    <div className="bg-black/30 rounded-lg p-3">
                                      <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                        <User className="w-3 h-3" />
                                        Genero
                                      </div>
                                      <p className="font-medium capitalize">{selectedUserProfile.gender}</p>
                                    </div>
                                  )}
                                  {selectedUserProfile.location && (
                                    <div className="bg-black/30 rounded-lg p-3">
                                      <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                        <MapPin className="w-3 h-3" />
                                        Localizacao
                                      </div>
                                      <p className="font-medium">{selectedUserProfile.location}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Training Info */}
                            <div>
                              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                                Treino
                              </h3>
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="bg-black/30 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
                                      <Dumbbell className="w-3 h-3" />
                                      Frequencia Semanal
                                    </div>
                                    <p className="font-medium">{selectedUserProfile.weekly_frequency}x por semana</p>
                                  </div>
                                  <div className="bg-[#32CD32]/10 border border-[#32CD32]/20 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-[#32CD32] text-xs mb-2">
                                      <ClipboardList className="w-3 h-3" />
                                      Planos Gerados
                                    </div>
                                    <p className="font-medium text-xl text-[#32CD32]">{selectedUserPlansCount}</p>
                                  </div>
                                </div>
                                <div className="bg-black/30 rounded-lg p-4">
                                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
                                    <Target className="w-3 h-3" />
                                    Objetivos
                                  </div>
                                  <p className="text-sm">{selectedUserProfile.goals}</p>
                                </div>
                                {selectedUserProfile.injuries && (
                                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-orange-400 text-xs mb-2">
                                      <AlertTriangle className="w-3 h-3" />
                                      Lesões / Cuidados
                                    </div>
                                    <p className="text-sm text-orange-300">{selectedUserProfile.injuries}</p>
                                  </div>
                                )}
                                {selectedUserProfile.restrictions && (
                                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-yellow-400 text-xs mb-2">
                                      <AlertTriangle className="w-3 h-3" />
                                      Restrições
                                    </div>
                                    <p className="text-sm text-yellow-300">{selectedUserProfile.restrictions}</p>
                                  </div>
                                )}
                                {selectedUserProfile.preferred_cardio && selectedUserProfile.preferred_cardio.length > 0 && (
                                  <div className="bg-black/30 rounded-lg p-4">
                                    <div className="text-gray-500 text-xs mb-2">Cardio Preferido</div>
                                    <div className="flex flex-wrap gap-2">
                                      {selectedUserProfile.preferred_cardio.map(cardio => (
                                        <span key={cardio} className="px-2 py-1 bg-[#32CD32]/20 text-[#32CD32] rounded-full text-xs">
                                          {cardio}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Account Info */}
                            <div className="pt-4 border-t border-white/10">
                              <div className="flex justify-between text-xs text-gray-500">
                                <span>Registado em: {new Date(selectedUser?.created_at).toLocaleDateString('pt-PT')}</span>
                                <span>Strava: {selectedUser?.strava_connected ? 'Conectado' : 'Não conectado'}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <User className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-500">Este utilizador ainda não completou o perfil.</p>
                          </div>
                        )}

                        <div className="flex gap-3 pt-6">
                          <Button
                            variant="ghost"
                            onClick={closeProfileModal}
                            className="flex-1"
                          >
                            Fechar
                          </Button>
                          <Button
                            onClick={() => {
                              closeProfileModal();
                              setEditingUser(selectedUser);
                            }}
                            className="flex-1 bg-[#32CD32] hover:bg-[#2ab32a] text-black"
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Editar Plano
                          </Button>
                        </div>
                      </CardContent>
                    </>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map(plan => (
                <Card key={plan.id} className="bg-[#121212] border-white/10">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl">{plan.name}</CardTitle>
                        <CardDescription className="text-2xl font-bold text-[#32CD32] mt-1">
                          {plan.price > 0 ? `${plan.price.toFixed(2)}` : 'Grátis'}
                        </CardDescription>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditPlan(plan)}
                        className="text-gray-400 hover:text-white"
                        data-testid={`edit-plan-${plan.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="text-sm text-gray-500">
                        Mensagens/dia: {plan.daily_messages === -1 ? 'Ilimitado' : plan.daily_messages}
                      </div>
                      <div className="text-sm text-gray-500">
                        Planos/mês: {plan.monthly_plans === -1 ? 'Ilimitado' : (plan.monthly_plans ?? '-')}
                      </div>
                      <div className="pt-3 border-t border-white/10">
                        <p className="text-xs text-gray-500 mb-2">Funcionalidades:</p>
                        <ul className="space-y-1">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="text-sm text-gray-300 flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#32CD32]" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Edit Plan Modal */}
            {editingPlan && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                <Card className="w-full max-w-md bg-[#121212] border-white/10 max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <CardTitle className="text-lg">Editar Plano</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nome</Label>
                      <Input
                        value={planForm.name}
                        onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                        className="bg-black/50 border-white/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Preco (EUR)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={planForm.price}
                        onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                        className="bg-black/50 border-white/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mensagens por Dia (-1 = ilimitado)</Label>
                      <Input
                        type="number"
                        value={planForm.daily_messages}
                        onChange={(e) => setPlanForm({ ...planForm, daily_messages: e.target.value })}
                        className="bg-black/50 border-white/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Planos de Treino por Mês (-1 = ilimitado)</Label>
                      <Input
                        type="number"
                        value={planForm.monthly_plans}
                        onChange={(e) => setPlanForm({ ...planForm, monthly_plans: e.target.value })}
                        className="bg-black/50 border-white/20"
                        data-testid="plan-monthly-plans-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Funcionalidades (uma por linha)</Label>
                      <textarea
                        value={planForm.features}
                        onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
                        className="w-full h-32 p-3 bg-black/50 border border-white/20 rounded-md text-white resize-none"
                        placeholder="Funcionalidade 1&#10;Funcionalidade 2&#10;..."
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="ghost"
                        onClick={() => setEditingPlan(null)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleUpdatePlan}
                        className="flex-1 bg-[#32CD32] hover:bg-[#2ab32a] text-black"
                        data-testid="save-plan-btn"
                      >
                        Guardar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete user confirmation */}
      <Dialog open={!!deletingUser} onOpenChange={(open) => !open && setDeletingUser(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Apagar utilizador
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Vais apagar <strong className="text-white">{deletingUser?.email}</strong> e
              todos os dados associados (perfil, planos de treino, progressões e conversas).
              Esta ação é permanente.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-4">
            <Button
              variant="ghost"
              onClick={() => setDeletingUser(null)}
              className="text-gray-400 hover:text-white"
              data-testid="cancel-delete-user-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteUser}
              className="bg-red-500 hover:bg-red-600 text-white rounded-lg"
              data-testid="confirm-delete-user-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Apagar definitivamente
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminPage;
